import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pso_cart")) || [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [checkoutConfig, setCheckoutConfig] = useState(null);

  useEffect(() => {
    api.get("/paypal/config").then((r) => setCheckoutConfig(r.data)).catch(() => {
      setCheckoutConfig({ configured: false, onsite_checkout_enabled: false });
    });
  }, []);

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  useEffect(() => {
    localStorage.setItem("pso_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, opts = {}) => {
    if (!checkoutConfig?.onsite_checkout_enabled || product.purchase_url) return false;
    const variant = opts.variant || null;
    const qty = Math.max(1, opts.qty || 1);
    const variantLabel = variant?.label || product.format || "";
    const price = variant ? Number(variant.price) : Number(product.price);
    const image = (product.images && product.images[0]) || product.image || "";
    const key = `${product.id}::${variant?.label || ""}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        { key, id: product.id, name: product.name, price, image, variant_label: variantLabel, qty },
      ];
    });
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const updateQty = (key, qty) => {
    if (qty <= 0) return removeItem(key);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty } : i)));
  };

  const clearCart = () => setItems([]);

  const total = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const count = items.reduce((sum, i) => sum + i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clearCart, total, count, isOpen, openCart, closeCart, checkoutConfig }}>
      {children}
    </CartContext.Provider>
  );
};
